import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';

import LocalStorageService from '../cache/local_storage_service'
import Texts from "../../models/static/texts/texts";
import ApiRequestDs from "./type-declerations/requests";
import ApiResponseDs from "./type-declerations/response";
import {History, Location} from 'history';
import {routes} from '../../models/static/routes/app-routes'
import CacheService from "../cache/cache_service";
import {matchPath} from "react-router";
import EnvService from "../env";
import ToastService from "../toast";
import curlirize from 'axios-curlirize';
// @ts-ignore
import {CurlHelper} from 'axios-curlirize/src/lib/CurlHelper.js';
import Utils from "../utils/utils";
import AuthBlockerService, {AuthRequestStamp} from "../auth-blocker";


// @ts-ignore
curlirize(axios, () => false);

/** apiCallback methods */
export const apiMethods = {
    get: 'GET',
    post: 'POST',
    put: 'PUT',
    delete: 'DELETE',
    patch: 'PATCH',
};

/**
 * Makes an api request using the provided request data and returns a proper result based on the result of the api
 * response.
 * @param apiRequestDs the request of the api
 * @param retried whether the request is retried
 */
const apiCallback = async <T>(apiRequestDs: ApiRequestDs, {retried = false} = {}): Promise<undefined | ApiResponseDs<T | null>> => {
    const imputedApiRequestDs = imputeRequest(apiRequestDs);
    let request = await createApiRequestConfig(imputedApiRequestDs);
    const logger = imputedApiRequestDs.logger;

    // if the api requires login, wait for the auth to be resolved before making the api call. if the auth is already resolved, proceed with the api call immediately.
    if (imputedApiRequestDs.loginRequired) {
        try {
            const [waitPromise, ignore] = AuthBlockerService.waitForAuth();
            if (!ignore) {
                await waitPromise;
                request = await createApiRequestConfig(imputedApiRequestDs);
            }
        } catch {
            return await checkResData<T>(imputedApiRequestDs, request, {
                status: 401,
                data: {errorCode: 401}
            } as AxiosResponse<ApiResponseDs<T>>, retried);
        }
    }
    const authStamp = AuthBlockerService.captureRequestStamp(request.headers);
    try {
        logger?.log(`Making API call to ${request.url} with method ${request.method} and data`, request.data, request.headers);
        const response: AxiosResponse<ApiResponseDs<T>> = await axios.request<any, AxiosResponse<ApiResponseDs<T>>>(request);
        return await checkResData<T>(imputedApiRequestDs, request, response, retried, authStamp);
    } catch (e) {
        if (axios.isCancel(e) || !!request.cancelToken?.reason) {
            logger?.log(`API call to ${request.url} was cancelled`);
            return {
                resultFlag: false,
                data: null,
                message: null,
                headerTitle: null,
                errorCode: null,
                status: undefined,
                aborted: true,
            }
        }
        if ((e as any).message === 'Network Error') {
            (e as any).response = (e as any).response ?? {
                status: 502,
                message: (e as any).message,
            }
        }
        if (axios.isAxiosError(e)) {
            return await checkResData<T>(imputedApiRequestDs, request, e.response, retried, authStamp);
        }
        return await checkResData<T>(imputedApiRequestDs, request, undefined, retried, authStamp);
    }
};

/**
 * Gets the headers required for the api call
 * @param headers the extra headers
 */
const imputeHeaders = async (headers: Record<string, string | number>) => {
    return {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET, PUT, POST, DELETE, OPTIONS",
        ...headers,
        Authorization: LocalStorageService.get(LocalStorageService.keys.token) ?? "00000000-0000-0000-0000-000000000000",
    };
}

/**
 * Imputes the given api request with default values for the fields that are not provided.
 * @param apiRequestDs
 */
const imputeRequest = (apiRequestDs: ApiRequestDs): ApiRequestDs => {
    return {
        ...apiRequestDs,
        url: apiRequestDs.url ?? "",
        method: apiRequestDs.method ?? apiMethods.get,
        showErrorToast: apiRequestDs.showErrorToast ?? true,
        showSuccessToast: apiRequestDs.showSuccessToast ?? false,
        redirectToError: apiRequestDs.redirectToError ?? false,
        loginRequired: apiRequestDs.loginRequired ?? true,
    };
}

/**
 * Creates the config for the api call based on the given api request data structure.
 * @param apiRequestDs
 */
const createApiRequestConfig = async (apiRequestDs: ApiRequestDs): Promise<AxiosRequestConfig> => {
    const headersFromApiRequest = (await apiRequestDs.getHeaders?.(apiRequestDs)) ?? {};
    const imputedHeaders = await imputeHeaders(headersFromApiRequest);
    return {
        url: apiRequestDs.url,
        method: apiRequestDs.method,
        data: apiRequestDs.data,
        onDownloadProgress: apiRequestDs.onDownloadProgress,
        onUploadProgress: apiRequestDs.onUploadProgress,
        headers: imputedHeaders,
        cancelToken: apiRequestDs.cancelToken,
    } satisfies AxiosRequestConfig;
}

// TODO: REMOVE
// let test401 = 3;

/**
 * Checks the response data form the api. if the status if not 200 or the errorCode of the response object is not
 * 200, then logs the error, sends data to sentry and returns the erroneous response.
 *
 * @param apiRequestDs the request of the api
 * @param request the axios request config
 * @param response the axios response
 * @param retried whether the request is retried
 * @param authStamp the authentication stamp
 */
const checkResData = async <T>(
    apiRequestDs: ApiRequestDs,
    request: AxiosRequestConfig,
    response?: AxiosResponse<ApiResponseDs<T>> | undefined,
    retried?: boolean | undefined,
    authStamp?: AuthRequestStamp,
): Promise<ApiResponseDs<T | null> | undefined> => {
    const logger = apiRequestDs.logger;
    logger?.log(`Checking response data for ${request.url} with method ${request.method} and response ${response?.status} ${response?.statusText}`, response?.data);

    // TODO: REMOVE
    // if (apiRequestDs.url === bannerApis.getAll) {
    //     if (response && test401 > 0) {
    //         test401--;
    //         //  force unauthorized for testing.
    //         response.status = 401;
    //         debugger;
    //     }
    // }

    const showToasts = await createBoundedToasts(request, response, apiRequestDs.location, apiRequestDs.showErrorToast);
    let alreadyShownToast = false;

    /**
     * A 401 for the token that is current now pauses every tab.
     * A 401 for a token login already replaced retries once and does not pause or log the user out.
     * Returns the retry response when the call should stop here, or null to keep building the error result.
     */
    const settleUnauthorized = async (): Promise<{ handled: true; value: ApiResponseDs<T | null> | undefined } | {
        handled: false
    }> => {
        if (EnvService.DEV_useAuthBlocker) {
            const stamp = authStamp ?? AuthBlockerService.captureRequestStamp(request.headers);
            const action = AuthBlockerService.classifyAuthFailure(stamp.generation, stamp.authorization, retried ? 1 : 0);
            if (action === 'retry') {
                if (!apiRequestDs.loginRequired) {
                    return {handled: false};
                }
                return {handled: true, value: await apiCallback<T>(apiRequestDs, {retried: true})};
            }
            if (action === 'ignore') {
                return {handled: false};
            }
            if (apiRequestDs.loginRequired && action === 'pause') {
                try {
                    const [pausePromise, ignore] = AuthBlockerService.pause();
                    if (!ignore) {
                        await pausePromise;
                        return {handled: true, value: await apiCallback<T>(apiRequestDs, {retried: true})};
                    }
                } catch {
                    // proceed down to unauthorized handling
                }
            }
        }
        if (apiRequestDs.unAuthorizedCallback) {
            apiRequestDs.unAuthorizedCallback(response?.data, apiRequestDs.history, apiRequestDs.location);
            alreadyShownToast = true;
        } else {
            unAuthorizedResponse();
        }
        return {handled: false};
    };

    const parseValidationErrorAndShowToast = () => {
        const data = response?.data as any;
        let messageTitle = data?.headerTitle ?? Texts.serverErrorTitle;
        let message = data?.message ?? Texts.serverErrorMessage;
        // this is confluent errors. first check if the object has the errors and title fields
        if (data?.title) {
            messageTitle = data.title;
        }
        if (data?.errors && typeof data.errors === 'object') {
            message = '';
            for (const key of Object.keys(data.errors)) {
                message += `
                            <li style="margin-left: -38px;">
                                <span class="text-sm secondary-color-1">${key}</span>
                                <ul>
                                    ${data.errors[key]
                    .map((err: string) =>
                        `<li class="text-xs" style="margin-left: -28px;">
                                        ${err}
                                    </li>`
                    ).join('')}
                                </ul>
                            </li>
                            `;
            }
            message = `<ul>${message}</ul>`;
        }
        showToasts.error({
            title: messageTitle,
            message: message,
        });
        alreadyShownToast = true;
    }

    // Catch axios api call error
    if (!response) {
        if (apiRequestDs.showErrorToast) {
            showToasts.error({
                title: Texts.serverErrorTitle,
                message: Texts.serverErrorMessage,
            });
            alreadyShownToast = true;
        }
        if (apiRequestDs.redirectToError) {
            serverErrorResponse(apiRequestDs.location, apiRequestDs.history)
        }
        return undefined;
    }
    // successful result
    if (response.status.toString().startsWith('2') && !response.data.errorCode) {
        if (apiRequestDs.showSuccessToast) {
            showToasts.success({
                title: response.data?.headerTitle ?? Texts.serverSuccessTitle,
                message: response.data?.message ?? Texts.serverSuccessMessage,
            })
            alreadyShownToast = true;
        }
        return {
            ...response.data,
            showToast: showToasts,
        }; // successful result
    }
    // error cases
    else {
        if (!retried && response.status === 502) {
            return retryRequest(apiRequestDs);
        }
        switch (response.status) {
            // unAuthorized request
            case 401: {
                const settled = await settleUnauthorized();
                if (settled.handled) {
                    return settled.value;
                }
            }
                break;
            // access denied
            case 403:
                if (apiRequestDs.accessDeniedCallback) {
                    apiRequestDs.accessDeniedCallback(response.data, apiRequestDs.history, apiRequestDs.location);
                    alreadyShownToast = true;
                } else if (apiRequestDs.redirectToError) {
                    accessDeniedResponse(apiRequestDs.location, apiRequestDs.history);
                }
                break;
            case 400: {
                if (apiRequestDs.showErrorToast) {
                    parseValidationErrorAndShowToast()
                }
                break;
            }
            case 200:
                switch (response.data.errorCode) {
                    // unAuthorized request internally
                    case 401:
                    case -401:
                    case -50: {
                        const settled = await settleUnauthorized();
                        if (settled.handled) {
                            return settled.value;
                        }
                    }
                        break;
                    // access denied internally
                    case 403:
                    case -403:
                        if (apiRequestDs.accessDeniedCallback) {
                            apiRequestDs.accessDeniedCallback(response.data, apiRequestDs.history, apiRequestDs.location);
                            alreadyShownToast = true;
                        } else if (apiRequestDs.redirectToError) {
                            accessDeniedResponse(apiRequestDs.location, apiRequestDs.history);
                        }
                        break;
                    case 400:
                    case -400:
                    case -100: {
                        if (apiRequestDs.showErrorToast) {
                            parseValidationErrorAndShowToast()
                        }
                        break;
                    }
                    default:
                        if (apiRequestDs.redirectToError) {
                            serverErrorResponse(apiRequestDs.location, apiRequestDs.history);
                        }
                        break;
                }
                break;
            default:
                break;
        }
        if (!alreadyShownToast) {
            if (apiRequestDs.showErrorToast)
                showToasts.error({
                    title: response.data?.headerTitle ?? Texts.serverErrorTitle,
                    message: response.data?.message ?? Texts.serverErrorMessage,
                    hideTicketSubmission: (response?.status === 401 || response?.data?.errorCode === 401),
                });
        }

        return {
            resultFlag: response.data?.resultFlag ?? false,
            data: response.data?.data ?? null,
            message: response.data?.message ?? Texts.serverErrorMessage,
            headerTitle: response.data?.headerTitle ?? Texts.serverErrorTitle,
            errorCode: response.data?.errorCode ?? response.status,
            status: response.status,
            showToast: showToasts,
        };
    }
}

/**
 * Retries the request after a second.
 * @param apiRequestDs
 */
const retryRequest = async <T>(apiRequestDs: ApiRequestDs): Promise<ApiResponseDs<T | null> | undefined> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return apiCallback<T>(apiRequestDs, {retried: true});
}

let screenshotPromise: Promise<string | null> | undefined;
let lastScreenshot: { data: string | null; timestamp: number } | undefined;

/**
 * Generates toasts that are bounded to the given arguments.
 * @param request
 * @param response
 * @param location
 * @param showError
 */
const createBoundedToasts = async (
    request: AxiosRequestConfig,
    response: AxiosResponse<ApiResponseDs<any>> | undefined,
    location?: Location | null,
    showError?: boolean,
) => {
    let capturedScreen: string | null = null;
    if (showError && responseErroneous(response) && EnvService.DEV_showErrorTicketSubmission) {
        const now = Date.now();

        // If we have a recent screenshot (within 1 second), reuse it instead of capturing again
        if (lastScreenshot && (now - lastScreenshot.timestamp) < 2000) {
            capturedScreen = lastScreenshot.data;
        } else {
            if (!screenshotPromise) {
                screenshotPromise = Utils.screenshotUserBrowserWindow();
            }
            try {
                const data = await screenshotPromise;
                capturedScreen = data;
                lastScreenshot = {
                    data,
                    timestamp: Date.now(),
                };
            } catch {
                capturedScreen = null;
            } finally {
                screenshotPromise = undefined;
            }
        }
    }
    return ({
        success: (props: any) => ToastService.success({
            ...(props ?? {}),
            props: {
                ...(props?.props ?? {}),
                data: {
                    ...(props?.props?.data ?? {}),
                    curl: generateCurl(request, response),
                    location: location,
                    apiResponse: response?.data,
                    apiRequest: request,
                    response: response,
                },
            },
        }),
        error: (props: any) => {
            return ToastService.error({
                hideTicketSubmission: false,
                ...(props ?? {}),
                props: {
                    ...(props?.props ?? {}),
                    data: {
                        ...(props?.props?.data ?? {}),
                        curl: generateCurl(request, response),
                        location: location,
                        capturedScreen: capturedScreen,
                        apiResponse: response?.data,
                        apiRequest: request,
                        response: response,
                    },
                },
            })
        },
    })
}

/**
 * Determines whether the given response is erroneous.
 * @param response
 */
const responseErroneous = (response: AxiosResponse<ApiResponseDs<any>> | undefined) => {
    return !response ||
        response.status.toString().startsWith('4') ||
        response.status.toString().startsWith('5') ||
        !response.data?.resultFlag
}

/**
 * Generates a C-URL from the response or request of the Axios.
 *
 * @param request
 * @param response
 */
const generateCurl = (request: AxiosRequestConfig, response: AxiosResponse<ApiResponseDs<any>> | undefined): string | undefined => {
    if (!response) {
        return new CurlHelper(request).generateCommand();
    }
    const config: AxiosRequestConfig & {
        curlCommand: string,
    } = response.config as any;
    return config?.curlCommand;
}

/**
 * Determines where the user should be navigated to in case of access denied response.
 * @param location
 * @param history
 */
const accessDeniedResponse = (location?: Location | null, history?: History | null) => {
    if (location && matchPath(location.pathname, {path: routes.auth.base})) {
        // in case the user is already in the auth page, we don't need to go to the auth page again
        return
    }
    if (location && location.pathname.includes(routes.error.accessDeniedForSection)) {
        // in case the user is already in access denied section, do nothing.
        return
    }
    if (history && location) {
        let path = location.pathname + routes.error.accessDeniedForSection;
        if (path.startsWith('//')) {
            path = path.slice(1);
        }
        history.push(path);
    }
}


/**
 * Determines where the user should be navigated to in case of a server error response.
 * @param location
 * @param history
 */
const serverErrorResponse = (location?: Location | null, history?: History | null) => {
    if (location && matchPath(location.pathname, {path: routes.auth.base})) {
        // in case the user is already in the auth page, we don't need to go to the auth page again
        return
    }
    if (location && location.pathname.includes(routes.error.serverErrorForSection)) {
        // in case the user is already in server error section, do nothing.
        return
    }
    if (history && location) {
        let path = location.pathname + routes.error.serverErrorForSection;
        if (path.startsWith('//')) {
            path = path.slice(1);
        }
        history.push(path);
    }
}


/**
 * Caches the current url and then removes the login information from the localstorage. Redirects the user to the
 * base of auth at the end.
 */
const unAuthorizedResponse = () => {
    if (EnvService.bypassAuth) {
        return;
    }
    void CacheService.logout(false);
}

export default apiCallback;
